/**
 * realSalesUniBoxApi — a UniBoxApi backed by the real n8n-proxy service.
 *
 * Data flow:
 *   1. getFeed() → ONE campaign_metrics call + ONE leads_cards call (n8n
 *      returns ALL campaigns in one bundle per the WORKAROUND documented in
 *      n8nDataFetchers.ts). Exactly 2 calls total, regardless of campaign
 *      count. The leads are mapped through the same leadToUniBoxMapper the
 *      mock path uses.
 *   2. getThread() → stubbed — returns empty until the thread mapping is
 *      defined. The ISR drawer opens fine without threads: its Briefing and
 *      Campaign tabs are the primary content.
 *   3. getSuggestions() → stubbed — returns empty.
 *
 * Errors are surfaced via a thrown error so the tab-level error handler
 * (SalesUniBoxTab) can catch and display a banner. The toggle stays
 * interactive during the error state so the user can switch back to mock.
 */

import type { UniBoxApi, UniBoxFeed, UniBoxEntity, UniBoxGroup, UniBoxThread } from '../../components/UniBox/types';
import { n8nService } from '../../components/core/services/n8n.service';
import { mapCampaignToUniBoxGroup, mapLeadCardToUniBoxEntity } from './leadToUniBoxMapper';
import type { CampaignMetrics, LeadCardResponse } from '../../types/n8nResponses';

const emptyFeed = (): UniBoxFeed => ({ campaigns: [], leads: [] });

/**
 * Build the UniBoxApi for the real data path. `userEmail` identifies the
 * owner of the dataset (mirrors the real client_email param).
 */
export const createRealSalesApi = (userEmail: string): UniBoxApi => ({
  /**
   * Load the whole Sales board: every campaign (as groups) + every lead across
   * all campaigns (as entities). Exactly 2 network calls: one campaign_metrics,
   * one leads_cards (which returns ALL campaigns in the bundle).
   */
  getFeed: async (): Promise<UniBoxFeed> => {
    // Fire both calls concurrently — they're independent.
    const [metricsResponse, leadsResponse] = await Promise.all([
      n8nService.getCampaignMetrics({ client_email: userEmail }),
      n8nService.getLeadsCards({ campaign_id: '', client_email: userEmail }),
    ]);

    if (!metricsResponse.success || !metricsResponse.data) {
      throw new Error(metricsResponse.error || 'Failed to fetch campaign metrics');
    }

    const campaigns: CampaignMetrics[] = metricsResponse.data.campaigns || [];
    const groups: UniBoxGroup[] = [];
    const entities: UniBoxEntity[] = [];

    // Build a map of campaign_id → leadsCardResponse[] from the bundle.
    let leadsByCampaign: Map<string, LeadCardResponse[]> = new Map();

    if (leadsResponse.success && leadsResponse.data) {
      // The bundle shape: the response carries a campaigns[] array where each
      // entry has campaign_id + leads[].
      const bundle = (leadsResponse.data as any)?.campaigns;
      if (Array.isArray(bundle)) {
        bundle.forEach((entry: any) => {
          const cid = entry.campaign_id;
          if (!cid) return;
          const campaignLeads: LeadCardResponse[] = (entry.leads || []).filter(
            (l: any): l is LeadCardResponse => l && typeof l === 'object',
          );
          leadsByCampaign.set(cid, campaignLeads);
        });
      } else {
        // Fallback: the response has a flat leads[] array.
        const flatLeads: LeadCardResponse[] = (leadsResponse.data.leads || []).filter(
          (l: any): l is LeadCardResponse => l && typeof l === 'object',
        );
        leadsByCampaign = new Map();
        flatLeads.forEach((lead) => {
          const cid = lead.campaign_id;
          if (!cid) return;
          const existing = leadsByCampaign.get(cid) || [];
          existing.push(lead);
          leadsByCampaign.set(cid, existing);
        });
      }
    }

    campaigns.forEach((campaign) => {
      const campaignLeads = leadsByCampaign.get(campaign.campaign_id) || [];
      groups.push(mapCampaignToUniBoxGroup(campaign, campaignLeads));
      campaignLeads.forEach((card) => entities.push(mapLeadCardToUniBoxEntity(card)));
    });

    return { campaigns: groups, leads: entities } as unknown as UniBoxFeed;
  },

  /**
   * Thread — stubbed until the mapping from N8N's per-channel outreach fields
   * (email_message_N / email_outreached_N) to UniBoxMessage[] is defined. The
   * ISR drawer opens fine without threads — its Briefing and Campaign tabs are
   * the primary content.
   */
  getThread: async (lvl1_id: number, _channel: string): Promise<UniBoxThread> => ({
    lvl1_id,
    channel: _channel,
    messages: [],
  }),

  /** Suggestions — stubbed for now. */
  getSuggestions: async (_lvl1_id: number, _channel: string): Promise<string[]> => [],
});

export default createRealSalesApi;